<template>
	<section class="relative min-h-[650px]">
		<div
			inert
			aria-hidden="true"
			class="pointer-events-none select-none blur-sm"
			data-test="safe-dashboard"
		>
			<h1 class="mb-6 font-heading text-3xl font-extrabold normal-case">
				{{
					$user.firstname
						? $t('dashboard.welcome-name', {
								name: $user.firstname
						  })
						: $t('dashboard.welcome')
				}}
			</h1>
			<div
				class="mx-auto flex max-w-4xl flex-col items-center gap-6 pt-12 text-center"
			>
				<div
					class="flex h-20 w-20 items-center justify-center rounded-2xl bg-info-subtle"
				>
					<IconSuitcase size="40" class="text-[#2C5060]" />
				</div>
				<div>
					<h2 class="mb-3 text-2xl font-semibold normal-case">
						{{ $t('dashboard.empty.title') }}
					</h2>
					<p>{{ $t('dashboard.empty.description') }}</p>
				</div>
				<div class="grid w-full grid-cols-1 gap-6 sm:grid-cols-3">
					<div
						v-for="step in ['create', 'publish', 'hire']"
						:key="step"
						class="rounded-xl border-2 border-grey-200 p-6"
					>
						<div
							class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-info-subtle"
						>
							<Component
								:is="stepIcons[step]"
								size="24"
								class="text-[#2C5060]"
							/>
						</div>
						<h3
							class="mb-3 font-sans text-base font-semibold normal-case"
						>
							{{ $t(`dashboard.empty.${step}-title`) }}
						</h3>
						<p class="mb-0 text-sm">
							{{ $t(`dashboard.empty.${step}-description`) }}
						</p>
					</div>
				</div>
				<span
					class="flex items-center rounded-lg bg-grey-1000 px-4 py-3 text-sm text-white"
					><IconPlus size="16" class="mr-2" />{{
						$t('dashboard.empty.create-action')
					}}</span
				>
			</div>
		</div>
		<div
			class="absolute inset-0 flex items-start justify-center bg-white/60 px-4 pt-12 sm:pt-20"
		>
			<section
				aria-labelledby="organization-unavailable-title"
				aria-describedby="organization-unavailable-description"
				class="w-full max-w-[500px] rounded-xl border border-grey-200 bg-white p-6 shadow-lg"
			>
				<div
					class="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-[20px] bg-info-subtle"
				>
					<IconLock size="28" class="text-[#2C5060]" />
				</div>
				<h1
					id="organization-unavailable-title"
					class="mb-3 text-center font-sans text-xl font-semibold normal-case"
				>
					{{ $t('organization.orphaned.title') }}
				</h1>
				<div
					id="organization-unavailable-description"
					class="mb-6 text-center text-sm text-grey-800"
				>
					<p class="mb-0">
						{{ $t('organization.orphaned.description') }}
					</p>
					<p class="mb-0">
						{{ $t('organization.orphaned.guidance') }}
					</p>
				</div>
				<div
					class="mb-6 grid grid-cols-[40px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-xl border border-grey-100 bg-grey-50 p-3 sm:grid-cols-[40px_minmax(0,1fr)_auto]"
				>
					<Avatar
						:image="$user.avatar"
						:fallback="initials"
						fallback-background-class="bg-grey-200"
						class="account-avatar"
						size="40px"
					/>
					<div class="min-w-0 flex-1 text-sm">
						<div class="break-words">
							{{ displayName }}
						</div>
						<div class="break-all text-grey-700">
							{{ $user.email }}
						</div>
					</div>
					<span
						class="col-start-2 justify-self-start rounded-full bg-red-50 px-2 py-1 text-xs text-red-900 sm:col-start-auto"
						>{{ $t('organization.orphaned.badge') }}</span
					>
				</div>
				<Button
					type="primary"
					class="w-full justify-center"
					@click="$store.dispatch('user/logout')"
				>
					<IconLogout size="20" class="mr-2" />
					{{ $t('organization.orphaned.logout') }}
				</Button>
			</section>
		</div>
	</section>
</template>
<script>
import Avatar from '~/components/avatar'
import Button from '~/components/ui/common/Button'
import {
	IconSuitcase,
	IconFile,
	IconCheckCircleAlt,
	IconUserCheck,
	IconPlus,
	IconLock,
	IconLogout
} from '~/components/Icons'
export default {
	components: {
		Avatar,
		Button,
		IconSuitcase,
		IconPlus,
		IconLock,
		IconLogout
	},
	data() {
		return {
			stepIcons: {
				create: IconFile,
				publish: IconCheckCircleAlt,
				hire: IconUserCheck
			}
		}
	},
	computed: {
		nameParts() {
			return [this.$user.firstname, this.$user.lastname].filter(Boolean)
		},
		displayName() {
			return this.$user.fullName || this.nameParts.join(' ')
		},
		initials() {
			return this.nameParts
				.map(part => part[0])
				.join('')
				.toUpperCase()
		}
	}
}
</script>

<style scoped>
.account-avatar >>> span {
	@apply text-base text-grey-800 !important;
}
</style>
